/**
 * The native side of the voice provider: registers the Twilio device for the
 * current property and wires every `CapacitorTwilioVoice` event (plus the iOS
 * foreground nudge) to the provider's state.
 *
 * A hook rather than provider body because it is one self-contained lifecycle
 * — one login, one listener set, one teardown — shaped like its
 * `useCallSimulators` sibling: it takes the provider's setters and refs and
 * owns nothing itself.
 */

import { App } from "@capacitor/app";
import type { PluginListenerHandle } from "@capacitor/core";
import { Capacitor } from "@capacitor/core";
import { CapacitorTwilioVoice } from "@capgo/capacitor-twilio-voice";
import type { Dispatch, SetStateAction } from "react";
import { useEffect } from "react";
import type { useHistory } from "react-router-dom";
import { toast } from "sonner";
import { interfaceTranslator } from "@/i18n/translate";
import { APP_PUSH_STATE } from "@/lib/history-entry";
import { routeTemplates } from "@/lib/routes";
import { parseCaller } from "./caller";
import { fetchTwilioVoiceToken } from "./client";
import { ensureMicGranted } from "./microphone";
import type { ActiveCall, PendingInvite } from "./voice-context";

export function useVoiceNativeListeners({
  activeCallRef,
  activePropertyId,
  historyRef,
  setActiveCall,
  setPendingInvite,
  setRegistered,
  supported,
  updateCall,
}: {
  activeCallRef: { current: ActiveCall | null };
  activePropertyId: string | null;
  historyRef: { current: ReturnType<typeof useHistory> };
  setActiveCall: Dispatch<SetStateAction<ActiveCall | null>>;
  setPendingInvite: Dispatch<SetStateAction<PendingInvite | null>>;
  setRegistered: Dispatch<SetStateAction<boolean>>;
  supported: boolean;
  updateCall: (updater: (prev: ActiveCall | null) => ActiveCall | null) => void;
}): void {
  // biome-ignore lint/correctness/useExhaustiveDependencies: activePropertyId is part of the Twilio identity — re-running this effect when it changes re-registers the device under the new compound identity.
  useEffect(() => {
    if (!supported) {
      return;
    }

    const listeners: PluginListenerHandle[] = [];
    let _cancelled = false;

    const register = async () => {
      try {
        // No permission prompts here. Requesting microphone +
        // POST_NOTIFICATIONS on AuthenticatedShell mount (right after
        // login) would hit a brand-new user with sensor-permission
        // dialogs before they did anything needing them. Mic is requested
        // just-in-time inside `makeCall` / `acceptInvite` (and via
        // `getUserMedia` for voice notes); notification permission via the
        // native OS dialog in `useNotificationPermissionPrompt`. Twilio
        // device registration itself only needs the access token + push
        // credentials and works fine without either permission.
        const { token } = await fetchTwilioVoiceToken();
        console.log("[voice] calling login()");
        await CapacitorTwilioVoice.login({ accessToken: token });
        console.log(
          "[voice] login resolved, waiting for registrationSuccess event"
        );
        // Do NOT setRegistered(true) here — login success just means the
        // token is stored. Real push registration is async; the plugin's
        // registrationSuccess listener flips the flag when it completes.
      } catch (err) {
        console.error("[voice] login failed", err);
      }
    };

    const attach = async () => {
      listeners.push(
        await CapacitorTwilioVoice.addListener("registrationSuccess", () => {
          console.log("[voice] registrationSuccess");
          if (_cancelled) {
            return;
          }
          setRegistered(true);
        }),
        await CapacitorTwilioVoice.addListener(
          "registrationFailure",
          ({ error }: { error?: string }) => {
            console.error("[voice] registration failure", error);
            if (_cancelled) {
              return;
            }
            setRegistered(false);
          }
        ),
        await CapacitorTwilioVoice.addListener(
          "callInviteReceived",
          ({
            callSid,
            from,
            to,
            customParams,
          }: {
            callSid: string;
            from?: string;
            to?: string;
            customParams?: Record<string, string>;
          }) => {
            if (_cancelled) {
              return;
            }
            // Parse the raw invite fields into a clean caller struct once,
            // here — the UI then branches on `caller.kind` and reads resolved
            // attributes without re-interpreting `from` / custom params.
            setPendingInvite({
              caller: parseCaller({ customParams, from, to }),
              callSid,
            });
            const h = historyRef.current;
            if (h.location.pathname !== routeTemplates.app.incomingCall) {
              h.push(routeTemplates.app.incomingCall, APP_PUSH_STATE);
            }
          }
        ),
        await CapacitorTwilioVoice.addListener("callInviteCancelled", () => {
          if (_cancelled) {
            return;
          }
          setPendingInvite(null);
          const h = historyRef.current;
          if (h.location.pathname === routeTemplates.app.incomingCall) {
            // Ionic's IonRouterOutlet doesn't pop on history.replace — the
            // IncomingCallPage stays mounted underneath and ends up as a blank
            // page once its content state is cleared. goBack actually unwinds
            // the stack with the proper transition.
            h.goBack();
          }
        }),
        await CapacitorTwilioVoice.addListener(
          "outgoingCallInitiated",
          ({
            callSid,
            to,
            displayName,
          }: {
            callSid: string;
            to: string;
            displayName?: string;
          }) => {
            if (_cancelled) {
              return;
            }
            setActiveCall({
              callSid,
              direction: "outgoing",
              displayName,
              muted: false,
              startedAt: null,
              status: "connecting",
              to,
            });
          }
        ),
        await CapacitorTwilioVoice.addListener(
          "callRinging",
          ({ callSid }: { callSid: string }) => {
            if (_cancelled) {
              return;
            }
            updateCall((prev) =>
              prev && prev.callSid === callSid
                ? { ...prev, status: "ringing" }
                : prev
            );
          }
        ),
        await CapacitorTwilioVoice.addListener(
          "callConnected",
          ({ callSid }: { callSid: string }) => {
            if (_cancelled) {
              return;
            }
            updateCall((prev) =>
              prev && prev.callSid === callSid
                ? { ...prev, startedAt: Date.now(), status: "connected" }
                : {
                    callSid,
                    direction: "incoming",
                    muted: false,
                    startedAt: Date.now(),
                    status: "connected",
                  }
            );
            setPendingInvite(null);
            const h = historyRef.current;
            if (h.location.pathname === routeTemplates.app.incomingCall) {
              h.goBack();
            }
          }
        ),
        await CapacitorTwilioVoice.addListener(
          "callReconnecting",
          ({ callSid }: { callSid: string }) => {
            if (_cancelled) {
              return;
            }
            updateCall((prev) =>
              prev && prev.callSid === callSid
                ? { ...prev, status: "reconnecting" }
                : prev
            );
          }
        ),
        await CapacitorTwilioVoice.addListener(
          "callReconnected",
          ({ callSid }: { callSid: string }) => {
            if (_cancelled) {
              return;
            }
            updateCall((prev) =>
              prev && prev.callSid === callSid
                ? { ...prev, status: "connected" }
                : prev
            );
          }
        ),
        await CapacitorTwilioVoice.addListener("callDisconnected", () => {
          if (_cancelled) {
            return;
          }
          setActiveCall(null);
          setPendingInvite(null);
          const h = historyRef.current;
          if (h.location.pathname === routeTemplates.app.incomingCall) {
            h.goBack();
          }
        }),
        await CapacitorTwilioVoice.addListener(
          "outgoingCallFailed",
          ({ reason }: { reason?: string }) => {
            if (_cancelled) {
              return;
            }
            setActiveCall(null);
            if (reason) {
              console.error("[voice] outgoing call failed", reason);
            }
            toast.error(
              interfaceTranslator("communications.nativeListeners")(
                "errorCallFailed"
              )
            );
          }
        )
      );

      // Recovery for the iOS CallKit lock-screen answer. An incoming call
      // accepted from the native call UI never passes through
      // `acceptInvite`, so the mic is never requested and the call connects
      // with a silent input. iOS only presents the permission dialog while
      // the app is foreground-active, so we ask the moment it becomes
      // active with a live incoming call. Granting won't restore audio for
      // *this* call (the audio session is already running without input),
      // but every subsequent call then has two-way audio. No-op on Android,
      // where the plugin requests the mic at accept time.
      if (Capacitor.getPlatform() === "ios") {
        listeners.push(
          await App.addListener("appStateChange", ({ isActive }) => {
            if (_cancelled || !isActive) {
              return;
            }
            if (activeCallRef.current?.direction !== "incoming") {
              return;
            }
            void ensureMicGranted();
          })
        );
      }
    };

    attach().then(register);

    return () => {
      _cancelled = true;
      setRegistered(false);
      for (const l of listeners) {
        l.remove();
      }
      CapacitorTwilioVoice.logout().catch(() => {
        // best-effort logout on unmount; nothing to do if it fails
      });
    };
  }, [supported, updateCall, activePropertyId]);
}
